import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon, CheckCircle, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadForm = ({ className = '' }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    property_size: '',
    service_needed: '',
    preferred_date: null,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, preferred_date: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.phone || !formData.email || !formData.property_size || !formData.service_needed) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        preferred_date: formData.preferred_date ? format(formData.preferred_date, 'yyyy-MM-dd') : null,
      };

      await axios.post(`${API}/leads`, submitData);
      setIsSubmitted(true);
      toast.success('Your request has been submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className={`bg-white rounded-2xl p-8 md:p-12 shadow-xl text-center ${className}`} data-testid="lead-form-success">
        <div className="w-20 h-20 bg-[#2E5C31]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-[#2E5C31]" />
        </div>
        <h3 className="text-2xl font-bold text-[#1A1A1A] mb-4 font-['Poppins']">
          Thank You!
        </h3>
        <p className="text-[#4A4A4A] mb-6">
          We've received your request for a free estimate. Our team will contact you within 24 hours.
        </p>
        <Button
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              full_name: '',
              phone: '',
              email: '',
              property_size: '',
              service_needed: '',
              preferred_date: null,
              message: '',
            });
          }}
          variant="outline"
          className="border-[#2E5C31] text-[#2E5C31] hover:bg-[#2E5C31]/10"
          data-testid="submit-another-btn"
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`bg-white rounded-2xl p-6 md:p-10 shadow-xl ${className}`}
      data-testid="lead-form"
      id="estimate-form"
    >
      <h3 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-2 font-['Poppins']">
        Get Your Free Estimate
      </h3>
      <p className="text-[#4A4A4A] mb-8">
        Fill out the form below and we'll get back to you within 24 hours.
      </p>

      <div className="space-y-5">
        {/* Full Name */}
        <div>
          <Label htmlFor="full_name" className="text-[#1A1A1A] font-medium">
            Full Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="full_name"
            name="full_name"
            value={formData.full_name}
            onChange={handleInputChange}
            placeholder="John Smith"
            className="mt-2 rounded-lg border-gray-200 focus:border-[#2E5C31] focus:ring-[#2E5C31]"
            data-testid="input-full-name"
            required
          />
        </div>

        {/* Phone & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="phone" className="text-[#1A1A1A] font-medium">
              Phone Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="(555) 123-4567"
              className="mt-2 rounded-lg border-gray-200 focus:border-[#2E5C31] focus:ring-[#2E5C31]"
              data-testid="input-phone"
              required
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-[#1A1A1A] font-medium">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="john@example.com"
              className="mt-2 rounded-lg border-gray-200 focus:border-[#2E5C31] focus:ring-[#2E5C31]"
              data-testid="input-email"
              required
            />
          </div>
        </div>

        {/* Property Size & Service */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[#1A1A1A] font-medium">
              Property Size <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.property_size}
              onValueChange={(value) => handleSelectChange('property_size', value)}
            >
              <SelectTrigger className="mt-2 rounded-lg border-gray-200 focus:border-[#2E5C31] focus:ring-[#2E5C31]" data-testid="select-property-size">
                <SelectValue placeholder="Select property size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small (Under 5,000 sq ft)</SelectItem>
                <SelectItem value="medium">Medium (5,000 - 10,000 sq ft)</SelectItem>
                <SelectItem value="large">Large (10,000 - 20,000 sq ft)</SelectItem>
                <SelectItem value="xlarge">Extra Large (Over 20,000 sq ft)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#1A1A1A] font-medium">
              Service Needed <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.service_needed}
              onValueChange={(value) => handleSelectChange('service_needed', value)}
            >
              <SelectTrigger className="mt-2 rounded-lg border-gray-200 focus:border-[#2E5C31] focus:ring-[#2E5C31]" data-testid="select-service">
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lawn-installation">Lawn Installation & Maintenance</SelectItem>
                <SelectItem value="landscape-design">Landscape Design</SelectItem>
                <SelectItem value="patio-hardscape">Patio & Hardscape Installation</SelectItem>
                <SelectItem value="irrigation">Irrigation Systems</SelectItem>
                <SelectItem value="seasonal-cleanup">Seasonal Yard Cleanup</SelectItem>
                <SelectItem value="tree-trimming">Tree & Shrub Trimming</SelectItem>
                <SelectItem value="multiple">Multiple Services</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preferred Date */}
        <div>
          <Label className="text-[#1A1A1A] font-medium">Preferred Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full mt-2 justify-start text-left font-normal rounded-lg border-gray-200 hover:border-[#2E5C31]"
                data-testid="date-picker-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4 text-[#2E5C31]" />
                {formData.preferred_date ? (
                  format(formData.preferred_date, 'PPP')
                ) : (
                  <span className="text-gray-500">Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.preferred_date}
                onSelect={handleDateChange}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Message */}
        <div>
          <Label htmlFor="message" className="text-[#1A1A1A] font-medium">
            Additional Details (Optional)
          </Label>
          <Textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Tell us more about your project..."
            className="mt-2 min-h-[100px] rounded-lg border-gray-200 focus:border-[#2E5C31] focus:ring-[#2E5C31]"
            data-testid="input-message"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#2E5C31] hover:bg-[#244A26] text-white rounded-full py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          data-testid="submit-estimate-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Get My Free Estimate'
          )}
        </Button>

        <p className="text-center text-sm text-[#888888] mt-4">
          We respect your privacy. Your information will only be used to respond to your inquiry.
        </p>
      </div>
    </form>
  );
};

export default LeadForm;
